import React, { useCallback, useMemo, useState } from 'react';
import { Settings, Target } from 'lucide-react';
import { getCosClassName, getCpaClassName, getMetricLabel, useComponentMetrics } from '../utils/metricsUtils';
import { calculateVariation, renderVariation } from '../utils/variationUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

const PAR_PAGE = 10;

/**
 * Tableau d'une entité ChatGPT Ads — groupes d'annonces, annonces.
 *
 * Piloté par les données qu'on lui passe : le chargement, le filtre par campagne et la
 * période de comparaison sont l'affaire du dashboard, pas de la table. Les colonnes et
 * leur ordre viennent de la configuration du module (`moduleId`), comme partout ailleurs.
 *
 * Le périmètre suit l'API : impressions, clics, dépense, conversions, chiffre d'affaires
 * attribué, et les ratios qui s'en déduisent — panier moyen, COS, ROAS et ROI compris.
 *
 * ## Pourquoi il est générique
 *
 * Il ne portait que les groupes d'annonces. Le tableau des annonces se lit un cran plus
 * bas dans la même hiérarchie, avec **les mêmes mesures, le même tri, la même pagination,
 * la même modale de métriques et le même appariement à la période précédente** : tout le
 * fichier, à cinq choses près — le titre, l'intitulé de la première colonne, l'icône, le
 * `moduleId` et un suffixe de classe CSS. Ces cinq-là sont devenues des entrées, et les
 * deux tableaux sont des préréglages (`ChatGPTAdGroupsTable`, `ChatGPTAdsTable`).
 *
 * Recopier aurait donné deux fichiers de 370 lignes à faire vieillir ensemble — et une
 * correction appliquée à un seul passe inaperçue, puisque l'autre continue d'afficher des
 * chiffres plausibles.
 */
const ChatGPTEntityTable = React.memo(({
  /** Les lignes à afficher, et celles de la période de comparaison. */
  lignes = [],
  lignesPrecedentes = null,
  /** Ce qui s'affiche : titre de section, en-tête de la première colonne, icône. */
  titre = "Groupes d'annonces",
  intituleColonne = "Groupe d'annonces",
  icone: Icone = Target,
  /** Suffixe de la classe de section — `chatgpt-<suffixe>-section`. */
  classeSuffixe = 'ad-groups',
  formatNumber,
  selectedAccount = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'chatgpt_ad_groups'
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [afficher, setAfficher] = useState(true);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  const componentMetrics = useComponentMetrics(moduleId, selectedAccount?.id || 'global');
  const estSelectionnee = useCallback(
    (cle) => componentMetrics.metrics.includes(cle),
    [componentMetrics.metrics]
  );
  const libelle = useCallback(
    (cle) => getMetricLabel(cle, moduleId, selectedAccount?.id || 'global'),
    [moduleId, selectedAccount?.id]
  );

  // Ligne de la période précédente, retrouvée par identifiant puis par nom : un compte
  // peut recréer une entité, l'identifiant change alors que le nom tient.
  const precedents = useMemo(() => {
    const index = {};
    for (const g of (lignesPrecedentes || [])) {
      if (g.id) index[g.id] = g;
      if (g.name) index[`nom:${g.name}`] = g;
    }
    return index;
  }, [lignesPrecedentes]);

  const comparaisonActive = compareEnabled && Array.isArray(lignesPrecedentes);
  const precedentDe = useCallback(
    (ligne) => precedents[ligne.id] || precedents[`nom:${ligne.name}`] || null,
    [precedents]
  );

  const metricConfig = useMemo(() => ({
    impressions: {
      sortKey: 'impressions',
      cellule: (g) => formatNumber(g.impressions || 0),
      total: (t) => <strong>{formatNumber(t.impressions)}</strong>,
      valeur: (g) => g.impressions || 0,
      totalValeur: (t) => t.impressions
    },
    ctr: {
      sortKey: 'ctr',
      cellule: (g) => `${formatNumber(g.ctr || 0, 2)}%`,
      total: (t) => <strong>{formatNumber(t.ctr, 2)}%</strong>,
      valeur: (g) => g.ctr || 0,
      totalValeur: (t) => t.ctr
    },
    clics: {
      sortKey: 'clicks',
      cellule: (g) => formatNumber(g.clicks || 0),
      total: (t) => <strong>{formatNumber(t.clicks)}</strong>,
      valeur: (g) => g.clicks || 0,
      totalValeur: (t) => t.clicks
    },
    cpc: {
      sortKey: 'cpc',
      cellule: (g) => `€${formatNumber(g.cpc || 0, 2)}`,
      total: (t) => <strong>€{formatNumber(t.cpc, 2)}</strong>,
      valeur: (g) => g.cpc || 0,
      totalValeur: (t) => t.cpc,
      // Une hausse de coût unitaire n'est pas une bonne nouvelle : variation inversée
      inverse: true
    },
    cout: {
      sortKey: 'cost',
      cellule: (g) => `€${formatNumber(g.cost || 0, 2)}`,
      total: (t) => <strong>€{formatNumber(t.cost, 2)}</strong>,
      valeur: (g) => g.cost || 0,
      totalValeur: (t) => t.cost
    },
    taux_conv: {
      sortKey: 'conversionRate',
      cellule: (g) => `${formatNumber(g.conversionRate || 0, 2)}%`,
      total: (t) => <strong>{formatNumber(t.conversionRate, 2)}%</strong>,
      valeur: (g) => g.conversionRate || 0,
      totalValeur: (t) => t.conversionRate
    },
    conv: {
      sortKey: 'conversions',
      cellule: (g) => formatNumber(g.conversions || 0, 1),
      total: (t) => <strong>{formatNumber(t.conversions, 1)}</strong>,
      valeur: (g) => g.conversions || 0,
      totalValeur: (t) => t.conversions
    },
    cpa: {
      sortKey: 'cpa',
      cellule: (g) => (g.conversions > 0 ? `€${formatNumber(g.cpa || 0, 2)}` : '-'),
      total: (t) => <strong>{t.conversions > 0 ? `€${formatNumber(t.cpa, 2)}` : '-'}</strong>,
      valeur: (g) => g.cpa || 0,
      totalValeur: (t) => t.cpa,
      classe: (g) => getCpaClassName(g.cpa || 0, selectedAccount?.id || 'global', moduleId),
      classeTotal: (t) => getCpaClassName(t.cpa, selectedAccount?.id || 'global', moduleId),
      inverse: true
    },
    /*
     * ─── Les cinq mesures marchandes ───────────────────────────────────────────────────
     *
     * Elles reposent toutes sur `conversionsValue`, que le backend tire de
     * `order_created_attributed_sales` (`/conversions/insights`). Ce dashboard les tenait
     * pour impossibles ; elles ne l'étaient pas, le champ manquait seulement à la lecture.
     *
     * **Un tiret, pas un zéro, quand le dénominateur manque.** Un annonceur qui ne déclare
     * pas de ventes a un chiffre d'affaires attribué nul : « 0,00 € » de panier moyen se
     * lirait comme un panier vide, et « 0 % » de COS comme une rentabilité parfaite. Le
     * tiret dit « pas de quoi calculer », ce qui est le constat exact — même convention que
     * le CPA au-dessus.
     */
    panier_moyen: {
      sortKey: 'avgOrderValue',
      cellule: (g) => (g.conversions > 0 && g.conversionsValue > 0 ? `€${formatNumber(g.avgOrderValue || 0, 2)}` : '-'),
      total: (t) => <strong>{t.conversions > 0 && t.conversionsValue > 0 ? `€${formatNumber(t.avgOrderValue, 2)}` : '-'}</strong>,
      valeur: (g) => g.avgOrderValue || 0,
      totalValeur: (t) => t.avgOrderValue
    },
    valeur_conv: {
      sortKey: 'conversionsValue',
      cellule: (g) => `€${formatNumber(g.conversionsValue || 0, 2)}`,
      total: (t) => <strong>€{formatNumber(t.conversionsValue, 2)}</strong>,
      valeur: (g) => g.conversionsValue || 0,
      totalValeur: (t) => t.conversionsValue
    },
    cos: {
      sortKey: 'cos',
      cellule: (g) => (g.conversionsValue > 0 ? `${formatNumber(g.cos || 0, 1)}%` : '-'),
      total: (t) => <strong>{t.conversionsValue > 0 ? `${formatNumber(t.cos, 1)}%` : '-'}</strong>,
      valeur: (g) => g.cos || 0,
      totalValeur: (t) => t.cos,
      classe: (g) => getCosClassName(g.cos || 0, selectedAccount?.id || 'global', moduleId),
      classeTotal: (t) => getCosClassName(t.cos, selectedAccount?.id || 'global', moduleId),
      // Dépenser une part croissante du chiffre d'affaires n'est pas une bonne nouvelle.
      inverse: true
    },
    roas: {
      sortKey: 'roas',
      cellule: (g) => (g.cost > 0 && g.conversionsValue > 0 ? `${formatNumber(g.roas || 0, 0)}%` : '-'),
      total: (t) => <strong>{t.cost > 0 && t.conversionsValue > 0 ? `${formatNumber(t.roas, 0)}%` : '-'}</strong>,
      valeur: (g) => g.roas || 0,
      totalValeur: (t) => t.roas
    },
    roi: {
      sortKey: 'roi',
      cellule: (g) => (g.cost > 0 && g.conversionsValue > 0 ? formatNumber(g.roi || 0, 2) : '-'),
      total: (t) => <strong>{t.cost > 0 && t.conversionsValue > 0 ? formatNumber(t.roi, 2) : '-'}</strong>,
      valeur: (g) => g.roi || 0,
      totalValeur: (t) => t.roi
    }
  }), [formatNumber, moduleId, selectedAccount?.id]);

  const colonnes = useMemo(
    () => componentMetrics.order.filter(cle => estSelectionnee(cle) && metricConfig[cle]),
    [componentMetrics.order, estSelectionnee, metricConfig]
  );

  const tries = useMemo(() => {
    const copie = [...lignes];
    copie.sort((a, b) => {
      if (sortConfig.key === 'name') {
        const va = String(a[sortConfig.key] || '').toLowerCase();
        const vb = String(b[sortConfig.key] || '').toLowerCase();
        return sortConfig.direction === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = Number(a[sortConfig.key]) || 0;
      const vb = Number(b[sortConfig.key]) || 0;
      return sortConfig.direction === 'asc' ? va - vb : vb - va;
    });
    return copie;
  }, [lignes, sortConfig]);

  const totalPages = Math.ceil(tries.length / PAR_PAGE);
  const pageCourante = Math.min(currentPage, Math.max(totalPages, 1));
  const affiches = useMemo(
    () => tries.slice((pageCourante - 1) * PAR_PAGE, pageCourante * PAR_PAGE),
    [tries, pageCourante]
  );

  /*
   * Le pied du tableau : sommes, puis **ratios de sommes, jamais moyennes de ratios**.
   *
   * Écrit une fois et appliqué aux deux périodes — il l'était deux fois, et ajouter les
   * mesures marchandes aurait été deux occasions de diverger sur un dénominateur.
   *
   * Les mêmes formules que `mesuresMarchandes` côté serveur, parce que la ligne et le
   * total doivent se répondre : un COS de pied calculé autrement que celui des lignes se
   * lit comme une erreur de données.
   */
  const totaliser = useCallback((entrees) => {
    const base = (entrees || []).reduce((acc, g) => ({
      impressions: acc.impressions + (g.impressions || 0),
      clicks: acc.clicks + (g.clicks || 0),
      cost: acc.cost + (g.cost || 0),
      conversions: acc.conversions + (g.conversions || 0),
      conversionsValue: acc.conversionsValue + (g.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...base,
      ctr: base.impressions > 0 ? (base.clicks / base.impressions) * 100 : 0,
      cpc: base.clicks > 0 ? base.cost / base.clicks : 0,
      conversionRate: base.clicks > 0 ? (base.conversions / base.clicks) * 100 : 0,
      cpa: base.conversions > 0 ? base.cost / base.conversions : 0,
      avgOrderValue: base.conversions > 0 ? base.conversionsValue / base.conversions : 0,
      cos: base.conversionsValue > 0 ? (base.cost / base.conversionsValue) * 100 : 0,
      roas: base.cost > 0 ? (base.conversionsValue / base.cost) * 100 : 0,
      roi: base.cost > 0 ? (base.conversionsValue - base.cost) / base.cost : 0
    };
  }, []);

  const totaux = useMemo(() => totaliser(lignes), [lignes, totaliser]);

  const totauxPrecedents = useMemo(
    () => (comparaisonActive && lignesPrecedentes?.length ? totaliser(lignesPrecedentes) : null),
    [comparaisonActive, lignesPrecedentes, totaliser]
  );

  const trier = useCallback((cle) => {
    setSortConfig(prev => ({
      key: cle,
      direction: prev.key === cle && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  }, []);

  const fleche = (cle) => (sortConfig.key === cle ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '');

  if (!lignes.length) return null;

  const moduleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !moduleVisible) return null;

  return (
    <div className={`campaigns-table-section chatgpt-${classeSuffixe}-section`}>
      <div className="section-header">
        <h4>
          <Icone size={16} />&nbsp;{titre}
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={moduleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title={titre}
            />
          ) : (
            <button
              onClick={() => setAfficher(!afficher)}
              className="section-toggle-btn"
              title={afficher ? 'Masquer la section' : 'Afficher la section'}
            >
              {afficher ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || moduleVisible) && afficher && (
        <>
          {comparaisonActive && (
            <div style={{ fontSize: '12px', color: '#64748b', margin: '0 0 8px' }}>
              Variations comparées à la {comparisonType === 'previous_year' ? 'même période l\'an dernier' : 'période précédente'}
            </div>
          )}

          <div className="campaigns-table-wrapper">
            <table className="campaigns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => trier('name')} className="sortable">
                    {intituleColonne}{fleche('name')}
                  </th>
                  {colonnes.map(cle => (
                    <th key={cle} onClick={() => trier(metricConfig[cle].sortKey)} className="sortable numeric">
                      {libelle(cle)}{fleche(metricConfig[cle].sortKey)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {affiches.map((ligne, index) => {
                  const precedent = comparaisonActive ? precedentDe(ligne) : null;
                  return (
                    <tr key={ligne.id || index}>
                      <td className="ad-group-name">{ligne.name || 'Sans nom'}</td>
                      {colonnes.map(cle => {
                        const config = metricConfig[cle];
                        return (
                          <td key={cle} className={`numeric ${config.classe ? config.classe(ligne) : ''}`}>
                            {config.cellule(ligne)}
                            {precedent && renderVariation(
                              calculateVariation(config.valeur(ligne), config.valeur(precedent)),
                              Boolean(config.inverse)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="totals-row">
                  <td className="ad-group-name"><strong>TOTAL</strong></td>
                  {colonnes.map(cle => {
                    const config = metricConfig[cle];
                    return (
                      <td key={cle} className={`numeric ${config.classeTotal ? config.classeTotal(totaux) : ''}`}>
                        {config.total(totaux)}
                        {totauxPrecedents && renderVariation(
                          calculateVariation(config.totalValeur(totaux), config.totalValeur(totauxPrecedents)),
                          Boolean(config.inverse)
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination-container" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              padding: '10px'
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={pageCourante === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: pageCourante === 1 ? '#f5f5f5' : 'white',
                  cursor: pageCourante === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Précédent
              </button>

              <span style={{ padding: '0 10px', fontSize: '14px' }}>
                Page {pageCourante} sur {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={pageCourante === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: pageCourante === totalPages ? '#f5f5f5' : 'white',
                  cursor: pageCourante === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId={moduleId}
          accountId={selectedAccount?.id || 'global'}
          title={titre}
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

ChatGPTEntityTable.displayName = 'ChatGPTEntityTable';

export default ChatGPTEntityTable;
